// src/pages/sales-order/CreateSalesOrder.jsx
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Tab, Nav } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import SaveOrderTable from "./SaveOrderTable";
import Loader from "../../components/Loader";
import { toast } from "../../components/AppToast";
import PartnerLookupField from "../../components/PartnerLookupField";
import LookupField from "../../components/LookupField";
import { CUSTOMER_LOOKUP, shipToLookupForCustomer } from "../../components/lookupConfigs";

// === schema imports ===
import {
    formSections,
    sourceAddressFieldsSO,
    destinationAddressFieldsSO,
    addItemsFieldsData,
} from "./formFields/salesOrderSchema.js";

// === services & utils ===
import {
    createSalesOrder,
    fetchSalesOrder,
    updateSalesOrder,
} from "../../services/salesOrder/salesOrder.js";
import { sanitizeFormData } from "../../utils/sanitizeFormData.js";
import { fetchDataAndPatchFieldForSo } from "../../services/item/item.js";
import SerialCaptureModal, { SerialCaptureButton } from "../../components/SerialCaptureModal";
import { compactSerialRows, countCapturedSerials, resolveSerialColumns } from "../../utils/serialCapture";
import { formatToInputDate, formatToDisplayDate } from "../../utils/dateFormatter.js";

// === modular tabs ===
import HeaderTab from "./tabs/HeaderTab";
import AddressTab from "./tabs/AddressTab";
import BillToTab from "./tabs/BillToTab";
import InvoiceTab from "./tabs/InvoiceTab";
import MiscTab from "./tabs/MiscTab";
import UdfTab from "./tabs/UdfTab";

/** Partner code for the signed-in user. Owner on an SO is a `partner_id`, not a mongo id. */
function resolveLoggedInOwner(user) {
    if (!user || typeof user !== "object") return "";
    const codeFrom = (value) => {
        if (!value) return "";
        if (typeof value === "string") {
            const text = value.trim();
            return /^[a-f0-9]{24}$/i.test(text) ? "" : text;
        }
        if (typeof value === "object") {
            return String(value.partner_id || value.owner || "").trim();
        }
        return "";
    };
    return (
        codeFrom(user.partner_id) ||
        codeFrom(user.owner) ||
        codeFrom(user.partnerDetails) ||
        codeFrom(user.partnerId) ||
        codeFrom(user.partner) ||
        ""
    );
}

export default function CreateSalesOrder({
    onCancel,
    onSaved,
    editData,
    initialData,
    readOnly = false,
}) {
    // prefer editData, fallback to initialData

    // console.log("......................................................", initialData)
    const effectiveEditData = editData ?? initialData ?? null;
    const isEditMode = !!(editData ?? initialData ?? null);
    const userDetail = useSelector((state) => state?.login?.userDetail);
    const loggedInOwner = resolveLoggedInOwner(userDetail);

    const [loading, setLoading] = useState(false);
    const [lineNumber, setLineNumber] = useState("00001");

    const statusToLabel = (v) => {
        if (v === 1 || `${v}` === "1") return "New";
        if (typeof v === "string" && v.trim().toLowerCase() === "new") return "New";
        if (v === 7 || `${v}` === "7") return "IN PROCESS";
        if (v === 5 || `${v}` === "5") return "COMPLETED";
        if (typeof v === "string") return v;
        return "";
    };

    const labelToStatusCode = (s) => {
        const v = (s ?? "").toString().toLowerCase().replace(/\s|-/g, "");
        if (v.includes("new")) return 1;
        if (v.includes("inprocess")) return 2;
        return 0;
    };

    // --- SO Counter with localStorage ---
    const [soCounter, setSoCounter] = useState(() => {
        const saved = localStorage.getItem("SO_COUNTER");
        return saved ? parseInt(saved, 10) : 1;
    });
    useEffect(() => {
      
        localStorage.setItem("SO_COUNTER", String(soCounter));
    }, [soCounter]);

    const formRef = useRef(null);

    // UI state: whether item add/edit form is visible (default: always open)
    const [showItemForm, setShowItemForm] = useState(true);

    // --- date helpers: input + display
    const toInputDate = (val, withTime = false) => {
        if (val === null || val === undefined || val === "") return "";
        try {
            const d = new Date(val);
            if (Number.isNaN(d.getTime())) return "";
            // input formats
            return withTime
                ? (formatToInputDate
                    ? formatToInputDate(d, true)
                    : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
                        d.getDate()
                    ).padStart(2, "0")}T${String(d.getHours()).padStart(2, "0")}:${String(
                        d.getMinutes()
                    ).padStart(2, "0")}`)
                : (formatToInputDate
                    ? formatToInputDate(d)
                    : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
                        d.getDate()
                    ).padStart(2, "0")}`);
        } catch {
            return "";
        }
    };

    const toDisplayDate = (val, withTime = false) => {
        if (!val) return "";
        try {
            const d = new Date(val);
            if (Number.isNaN(d.getTime())) return "";
            return withTime
                ? (formatToDisplayDate
                    ? formatToDisplayDate(d, true)
                    : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
                        d.getDate()
                    ).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(
                        d.getMinutes()
                    ).padStart(2, "0")}`)
                : (formatToDisplayDate
                    ? formatToDisplayDate(d)
                    : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
                        d.getDate()
                    ).padStart(2, "0")}`);
        } catch {
            return "";
        }
    };

    const generateSONumber = () => {
        const randomNum = Math.floor(1000 + Math.random() * 9000);
        return `SO${randomNum}`;
    };

    const getInitialFormData = (counter) => {
        let initialDataObj = {};
        formSections.forEach((section) => {
            section.fields.forEach((field) => {
                if (field.name === "soNumber") {
                    initialDataObj[field.name] = generateSONumber();
                } else if (field.name === "status" || field.name === "systemStatus") {
                    initialDataObj[field.name] = "New";
                } else if (field.name === "orderType") {
                    initialDataObj[field.name] = "SO";
                } else if (field.name === "owner") {
                    initialDataObj[field.name] = loggedInOwner;
                } else if (field.name === "expectedReceiptDate") {
                    const today = new Date();
                    initialDataObj[field.name] = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
                } else if (field.type === "date") {
                    initialDataObj[field.name] = "";
                } else {
                    initialDataObj[field.name] = field.defaultValue || "";
                }
            });
        });
        sourceAddressFieldsSO.forEach((f) => (initialDataObj[f.name] = initialDataObj[f.name] || ""));
        destinationAddressFieldsSO.forEach(
            (f) => (initialDataObj[f.name] = initialDataObj[f.name] || "")
        );
        // billTo defaults
        initialDataObj.billToName = initialDataObj.billToName || "";
        initialDataObj.billToAddress1 = initialDataObj.billToAddress1 || "";
        initialDataObj.billToCity = initialDataObj.billToCity || "";
        initialDataObj.billToState = initialDataObj.billToState || "";
        initialDataObj.billToCountry = initialDataObj.billToCountry || "";
        return initialDataObj;
    };

    const [formData, setFormData] = useState(getInitialFormData(soCounter));

    useEffect(() => {
        if (isEditMode || !loggedInOwner) return;
        setFormData((prev) => {
            if (prev.owner) return prev;
            return { ...prev, owner: loggedInOwner, orderType: prev.orderType || "SO" };
        });
    }, [isEditMode, loggedInOwner]);

    const getInitialItemFormData = (nextLine = null) => {
        let initialItem = {};
        addItemsFieldsData.forEach((field) => {
            if (field.name === "lineNumber") {
                const ln = nextLine ?? String(lineNumber).padStart(5, "0");
                initialItem[field.name] = ln;
            } else if (field.defaultValue !== undefined) {
                initialItem[field.name] = field.defaultValue;
            } else {
                initialItem[field.name] = field.type === "checkbox" ? false : "";
            }
        });
        // ensure both exist
        if (!("item" in initialItem)) initialItem.item = "";
        if (!("skuId" in initialItem)) initialItem.skuId = "";
        if (!initialItem.uom) initialItem.uom = "EA";
        initialItem.serialConfig = null;
        initialItem.serialDetails = [];
        return initialItem;
    };

    const [itemFormData, setItemFormData] = useState(getInitialItemFormData());
    const [savedData, setSavedData] = useState([]);
    const [activeTab, setActiveTab] = useState(0);
    const [open, setOpen] = useState(false);
    const [errors, setErrors] = useState({});
    const [itemErrors, setItemErrors] = useState({});
    const [topActiveSection, setTopActiveSection] = useState(formSections[0].title);
    const [hasTriedSubmit, setHasTriedSubmit] = useState(false);
    const [showSerialModal, setShowSerialModal] = useState(false);

    const shipToLookupConfig = React.useMemo(
        () => shipToLookupForCustomer(formData.sourceCustomerId),
        [formData.sourceCustomerId]
    );

    const soSerialEnabled =
        !!itemFormData.serialConfig?.outboundEnabled || (itemFormData.serialDetails?.length ?? 0) > 0;
    const soSerialColumns = React.useMemo(
        () => resolveSerialColumns(itemFormData.serialConfig?.outboundLabels, itemFormData.serialDetails),
        [itemFormData.serialConfig, itemFormData.serialDetails]
    );

    // ---------- Robust mapping: prefill header + addresses + billTo when effectiveEditData arrives ----------
    // Full useEffect to robustly map effectiveEditData into formData
    // Assumes helpers: toInputDate(date, includeTime), statusToLabel(status)
    // and state: effectiveEditData, setFormData

    useEffect(() => {
        if (!effectiveEditData) return;

        // Prefer nested soDetails if present, else use object itself
        const header = effectiveEditData.soDetails ? { ...effectiveEditData.soDetails } : { ...effectiveEditData };

        const source =
            effectiveEditData.sourceDetail ??
            effectiveEditData.source ??
            effectiveEditData.sourceLocation ??
            {};

        const dest =
            effectiveEditData.destination ??
            effectiveEditData.destinationDetail ??
            effectiveEditData.delivery ??
            {};

        const bill = effectiveEditData.billTo ?? effectiveEditData.bill_to ?? {};

        const carrier =
            (effectiveEditData.carrier && typeof effectiveEditData.carrier === "object" && effectiveEditData.carrier) ||
            (header.carrier && typeof header.carrier === "object" && header.carrier) ||
            {};

        // Debug prints to inspect payload shapes while developing
        console.log('--- effectiveEditData (incoming) ---');
        console.log('effectiveEditData:', effectiveEditData);
        console.log('header (soDetails fallback):', header);
        console.log('source:', source);
        console.log('dest:', dest);
        console.log('bill:', bill);
        console.log('udf (possible locations):', effectiveEditData.udf, header.udf, effectiveEditData.soDetails?.udf);
        console.log('invoice (possible locations):', effectiveEditData.invoice, header.invoice, effectiveEditData.soDetails?.invoice);
        console.log('misc (possible locations):', effectiveEditData.misc, header.misc, effectiveEditData.soDetails?.misc);

        setFormData((prev) => {
            const incomingBillToName =
                bill.billToName ??
                bill.name ??
                header.billToName ??
                effectiveEditData.billToName ??
                '';

            const incomingBillToAddress1 =
                bill.addressLine1 ??
                header.billToAddress1 ??
                effectiveEditData.billToAddress1 ??
                '';

            const incomingBillToAddress2 =
                bill.addressLine2 ??
                header.billToAddress2 ??
                effectiveEditData.billToAddress2 ??
                '';

            const incomingBillToCity =
                bill.city ?? header.billToCity ?? effectiveEditData.billToCity ?? '';

            const incomingBillToState =
                bill.state ?? header.billToState ?? effectiveEditData.billToState ?? '';

            const incomingBillToCountry =
                bill.country ?? header.billToCountry ?? effectiveEditData.billToCountry ?? '';

            const incomingBillToContact =
                bill.contact ?? header.billToContact ?? effectiveEditData.billToContact ?? '';

            const incomingBillToEmail =
                bill.email ?? header.billToEmail ?? effectiveEditData.billToEmail ?? '';

            // If billTo city/state/country are missing but destination has them, use destination as fallback
            const billToCityFinal = incomingBillToCity || dest.city || dest.cityName || '';
            const billToStateFinal = incomingBillToState || dest.state || '';
            const billToCountryFinal = incomingBillToCountry || dest.country || '';

            const sameAsDestinationDetected =
                !!(
                    (incomingBillToName && dest.locationName && incomingBillToName === (dest.locationName ?? '')) ||
                    (incomingBillToAddress1 && dest.addressLine1 && incomingBillToAddress1 === (dest.addressLine1 ?? '')) ||
                    (incomingBillToAddress2 && dest.addressLine2 && incomingBillToAddress2 === (dest.addressLine2 ?? ''))
                ) ||
                (!incomingBillToName && !incomingBillToAddress1 && (dest.locationName || dest.addressLine1));

            // Compute a best-effort order date (prefer header, fallback to effectiveEditData.createdAt)
            const computedOrderDate =
                header.orderDate ??
                header.order_date ??
                effectiveEditData.orderDate ??
                effectiveEditData.order_date ??
                effectiveEditData.createdAt ??
                null;

            // Determine top-level source value
            const computedSourceShort = (() => {
                let val = header.source ?? header.sourceCode ?? effectiveEditData.soSource ?? prev.source ?? "";
                if (val && typeof val === "object") {
                    val = val.sourceCode ?? val.code ?? val.source ?? "";
                }
                if (!val && source && typeof source === "object") {
                    val = source.sourceCode ?? source.code ?? (typeof source.source === "string" ? source.source : "") ?? "";
                }
                if (val && typeof val === "object") val = "";
                return val || prev.source || "";
            })();

            const computedSourceLocationName =
                (source && typeof source === "object" && (source.sourceName ?? source.locationName ?? source.name)) ||
                header.sourceLocationName ||
                effectiveEditData.sourceLocationName ||
                prev.sourceLocationName ||
                "";

            const computedDestinationLocationName = (() => {
                const fromDest =
                    dest.locationName ?? dest.name ?? dest.destinationName ?? "";
                if (fromDest) return fromDest;
                const fromTop =
                    effectiveEditData.destinationLocationName ??
                    effectiveEditData.destinationName ??
                    "";
                if (fromTop) return fromTop;
                if ((dest.addressLine1 || dest.addressLine2) && incomingBillToName) {
                    return incomingBillToName;
                }
                return prev.destinationName || prev.destinationLocationName || "";
            })();

            // Helper to safely read udf values with multiple casing/locations
            const readUdf = (keys) => {
                for (const k of keys) {
                    // try header.udf first, then effectiveEditData.udf, then soDetails.udf
                    const candidates = [header.udf, effectiveEditData.udf, effectiveEditData.soDetails?.udf, effectiveEditData];
                    for (const c of candidates) {
                        if (!c) continue;
                        if (Object.prototype.hasOwnProperty.call(c, k)) return c[k];
                    }
                }
                return undefined;
            };

            return {
                ...prev,

                // preserve header keys (prefer header and effectiveEditData)
                ...header,

                totalOrderedQuantity: header.totalOrderedQuantity ?? prev.totalOrderedQuantity ?? '',
                totalOrderQuantity:
                    header.totalOrderedQuantity ?? header.totalOrderQuantity ?? prev.totalOrderQuantity ?? '',

                // dates
                expectedReceiptDate:
                    toInputDate(
                        header.expectedReceiptDate ?? header.expected_receipt ?? effectiveEditData.expectedReceiptDate ?? effectiveEditData.expected_receipt,
                        false
                    ) || prev.expectedReceiptDate,

                // orderDate as datetime-local string
                orderDate: toInputDate(computedOrderDate, true) || prev.orderDate,

                // soNumber fallback
                soNumber: effectiveEditData.soNumber ?? header.soNumber ?? prev.soNumber ?? '',

                // external order fallback (safe access)
                externalOrder:
                    header.externalSoNumber ??
                    effectiveEditData.externalSoNumber ??
                    effectiveEditData.soDetails?.externalSoNumber ??
                    prev.externalOrder ??
                    '',

                // SOURCE
                source: computedSourceShort,

                status: statusToLabel(
                    header.status ?? effectiveEditData.status ?? effectiveEditData.soDetails?.status
                ) || "New",
                systemStatus: statusToLabel(
                    header.status ?? effectiveEditData.status ?? effectiveEditData.soDetails?.status
                ) || "New",

                sourceLocationName: computedSourceLocationName,
                sourceCustomerId:
                    (source && typeof source === "object" && source.customerId) ||
                    prev.sourceCustomerId ||
                    "",
                sourceCustomerCode:
                    (source && typeof source === "object" && (source.sourceCode || source.customerCode)) ||
                    prev.sourceCustomerCode ||
                    "",
                sourceAddress1: (source && (source.addressLine1 ?? source.address1)) || effectiveEditData.sourceAddress1 || prev.sourceAddress1 || '',
                sourceAddress2: (source && (source.addressLine2 ?? source.address2)) || effectiveEditData.sourceAddress2 || prev.sourceAddress2 || '',
                sourceAddress3: (source && source.addressLine3) || effectiveEditData.sourceAddress3 || prev.sourceAddress3 || '',
                sourceAddress4: (source && source.addressLine4) || effectiveEditData.sourceAddress4 || prev.sourceAddress4 || '',
                sourceCity: (source && source.city) ?? effectiveEditData.sourceCity ?? prev.sourceCity ?? '',
                sourceState: (source && source.state) ?? effectiveEditData.sourceState ?? prev.sourceState ?? '',
                sourceCountry: (source && source.country) ?? effectiveEditData.sourceCountry ?? prev.sourceCountry ?? '',
                sourceContact: (source && source.contact) ?? effectiveEditData.sourceContact ?? prev.sourceContact ?? '',
                sourceEmail: (source && source.email) ?? effectiveEditData.sourceEmail ?? prev.sourceEmail ?? '',

                // DESTINATION
                destinationLocationName: computedDestinationLocationName,
                destinationName: computedDestinationLocationName,
                destinationAddress1: (dest.addressLine1 ?? dest.address1) || effectiveEditData.destinationAddress1 || prev.destinationAddress1 || '',
                destinationAddress2: (dest.addressLine2 ?? dest.address2) || effectiveEditData.destinationAddress2 || prev.destinationAddress2 || '',
                destinationAddress3: (dest.addressLine3 ?? dest.address3) || effectiveEditData.destinationAddress3 || prev.destinationAddress3 || '',
                destinationAddress4: (dest.addressLine4 ?? dest.address4) || effectiveEditData.destinationAddress4 || prev.destinationAddress4 || '',
                destinationCity: dest.city ?? effectiveEditData.destinationCity ?? prev.destinationCity ?? '',
                destinationState: dest.state ?? effectiveEditData.destinationState ?? prev.destinationState ?? '',
                destinationCountry: dest.country ?? effectiveEditData.destinationCountry ?? prev.destinationCountry ?? '',
                destinationContact: dest.contact ?? effectiveEditData.destinationContact ?? prev.destinationContact ?? '',
                destinationEmail: dest.email ?? effectiveEditData.destinationEmail ?? prev.destinationEmail ?? '',

                // BILLTO
                billToName: incomingBillToName || prev.billToName || dest.locationName || '',
                billToAddress1: incomingBillToAddress1 || prev.billToAddress1 || dest.addressLine1 || '',
                billToAddress2: incomingBillToAddress2 || prev.billToAddress2 || dest.addressLine2 || '',
                billToCity: billToCityFinal || prev.billToCity || '',
                billToState: billToStateFinal || prev.billToState || '',
                billToCountry: billToCountryFinal || prev.billToCountry || '',
                billToContact: incomingBillToContact || prev.billToContact || dest.contact || '',
                billToEmail: incomingBillToEmail || prev.billToEmail || dest.email || '',

                owner: header.owner ?? effectiveEditData.owner ?? prev.owner ?? '',

                trailerNumber: header.trailerNumber ?? effectiveEditData.trailerNumber ?? prev.trailerNumber ?? '',
                trailerOwner: header.trailerOwner ?? effectiveEditData.trailerOwner ?? prev.trailerOwner ?? '',
                driverName: header.driverName ?? effectiveEditData.driverName ?? prev.driverName ?? '',
                proNumber: header.proNumber ?? effectiveEditData.proNumber ?? prev.proNumber ?? '',
                loadId: header.loadId ?? effectiveEditData.loadId ?? prev.loadId ?? '',
                door: header.door ?? effectiveEditData.door ?? prev.door ?? "",
                route: header.route ?? effectiveEditData.route ?? prev.route ?? "",
                stop: header.stop ?? effectiveEditData.stop ?? prev.stop ?? "",

                carrierCode: carrier.carrierCode || prev.carrierCode || "",
                carrierName: carrier.carrierName || prev.carrierName || "",
                carrierAddress1: carrier.addressLine1 || carrier.carrierAddress1 || prev.carrierAddress1 || "",
                carrierAddress2: carrier.addressLine2 || carrier.carrierAddress2 || prev.carrierAddress2 || "",
                carrierCity: carrier.city || carrier.carrierCity || prev.carrierCity || "",
                carrierState: carrier.state || carrier.carrierState || prev.carrierState || "",
                carrierCountry: carrier.country || carrier.carrierCountry || prev.carrierCountry || "",
                carrierPhone: carrier.phone || carrier.carrierPhone || prev.carrierPhone || "",
                carrierTrailerNumber: header.trailerNumber || carrier.trailerNumber || prev.carrierTrailerNumber || "",
                carrierTrailerOwner: header.trailerOwner || carrier.trailerOwner || prev.carrierTrailerOwner || "",
                carrierDriverName: header.driverName || carrier.driverName || prev.carrierDriverName || "",
                departureDateTime:
                    toInputDate(header.departureDateTime || carrier.departureDateTime, true) || prev.departureDateTime || "",
                actualDeliveryDate:
                    toInputDate(header.actualDeliveryDate || carrier.actualDeliveryDate, false) || prev.actualDeliveryDate || "",

                // UDF - robust to different casing / locations
                soStoHeaderUdf1:
                    readUdf(['textUdf1', 'textUDF1', 'headerUdf1', 'headerUDF1', 'headerUdf']) ?? prev.soStoHeaderUdf1 ?? '',
                soStoHeaderUdf2:
                    readUdf(['textUdf2', 'textUDF2', 'headerUdf2', 'headerUDF2']) ?? prev.soStoHeaderUdf2 ?? '',
                soStoHeaderUdf3:
                    readUdf(['textUdf3', 'textUDF3', 'headerUdf3', 'headerUDF3']) ?? prev.soStoHeaderUdf3 ?? '',
                soStoHeaderUdf4:
                    readUdf(['textUdf4', 'textUDF4', 'headerUdf4', 'headerUDF4']) ?? prev.soStoHeaderUdf4 ?? '',

                // Invoice mapping (explicit)
                invoiceNumber:
                    header.invoice?.invoiceNumber ?? effectiveEditData.invoice?.invoiceNumber ?? effectiveEditData.soDetails?.invoice?.invoiceNumber ?? prev.invoiceNumber ?? '',
                invoiceAmount:
                    header.invoice?.invoiceAmount ?? effectiveEditData.invoice?.invoiceAmount ?? effectiveEditData.soDetails?.invoice?.invoiceAmount ?? prev.invoiceAmount ?? '',
                invoiceType:
                    header.invoice?.invoiceType ?? effectiveEditData.invoice?.invoiceType ?? prev.invoiceType ?? '',
                invoicePaymentTerms:
                    header.invoice?.paymentTerms ?? effectiveEditData.invoice?.paymentTerms ?? prev.invoicePaymentTerms ?? '',
                invoiceDate:
                    toInputDate(
                        header.invoice?.invoiceDate ?? effectiveEditData.invoice?.invoiceDate ?? effectiveEditData.soDetails?.invoice?.invoiceDate,
                        false
                    ) || prev.invoiceDate || '',

                // Misc mapping
                miscNotes:
                    header.misc?.notes ?? effectiveEditData.misc?.notes ?? effectiveEditData.soDetails?.misc?.notes ?? prev.miscNotes ?? '',
                miscTermsOfDelivery:
                    header.misc?.termsOfDelivery ?? effectiveEditData.misc?.termsOfDelivery ?? prev.miscTermsOfDelivery ?? '',
                miscTermsOfPayment:
                    header.misc?.termsOfPayment ?? effectiveEditData.misc?.termsOfPayment ?? prev.miscTermsOfPayment ?? '',
                miscCreatedAt:
                    toInputDate(
                        header.misc?.createdAt ?? effectiveEditData.misc?.createdAt ?? effectiveEditData.soDetails?.misc?.createdAt,
                        true
                    ) || prev.miscCreatedAt || '',

                __activeAddressTab: prev.__activeAddressTab ?? 'supplier',

                sameAsDestination: prev.sameAsDestination ?? sameAsDestinationDetected,
            };
        });
    }, [effectiveEditData]);





    // ---------- Populate savedData (items) from effectiveEditData ----------
    useEffect(() => {
        if (!effectiveEditData) return;

        const incomingLines = Array.isArray(effectiveEditData.lineItems)
            ? effectiveEditData.lineItems
            : Array.isArray(effectiveEditData._raw?.lineItems)
                ? effectiveEditData._raw.lineItems
                : Array.isArray(effectiveEditData.items)
                    ? effectiveEditData.items
                    : Array.isArray(effectiveEditData.detail?.lineItems)
                        ? effectiveEditData.detail.lineItems
                        : [];

        const mapped = incomingLines.map((li, idx) => {
            const d = li.detail ?? li;

            const rawLn =
                d.lineNumber ?? d.line ?? d.detailLineNumber ?? (d.lineNumber === 0 ? "0" : null) ?? idx + 1;
            const ln = String(rawLn).padStart(5, "0");

            const sku = d.skuId ?? d.sku ?? d.item ?? d.productCode ?? "";
            const desc = d.description ?? d.title ?? d.name ?? "";
            const orderedQty =
                Number(d.orderedQty ?? d.orderedQuantity ?? d.orderQty ?? d.qty ?? 0) || 0;
            const openQty = Number(d.openQty ?? d.openQuantity ?? orderedQty) || orderedQty;

            const qc = (() => {
                const q = d.qcRequired ?? d.qc_required ?? d.qc;
                if (q === "Y" || q === "y") return true;
                return Boolean(q);
            })();

            const sellingPrice =
                d.sellingPrice ??
                d.customerPricing?.sellingPrice ??
                li.sellingPrice ??
                li.customerPricing?.sellingPrice ??
                "";

            return {
                _id: li._id ?? li.id ?? `${Date.now()}-${idx}`,
                id: li._id ?? li.id ?? ln,
                lineNumber: ln,
                item: sku,
                skuId: sku,
                description: desc,
                itemPerPrice:
                    sellingPrice !== "" && sellingPrice != null ? String(sellingPrice) : "",
                itemcode: d.itemCode ?? d.itemcode ?? "",
                itemweight: d.itemWeight ?? d.itemweight ?? d.weight ?? "",
                pack: d.pack ?? "STD",
                orderQty: orderedQty,
                openQty: openQty,

                itemPOTolerance: d.itemPOTolerance ?? d.tolerance ?? null,
                qcRequired: qc,
                uom: d.uom ?? "EA",
                status: statusToLabel(d.status ?? li.status),
                serialDetails: Array.isArray(d.serialDetails) ? d.serialDetails : [],
                _raw: li,
            };
        });

        setSavedData(mapped);
        setOpen(mapped.length > 0);
        setItemErrors({});

        // update next lineNumber
        const maxLine = mapped.reduce((max, r) => {
            const n = parseInt(r.lineNumber || "0", 10);
            return Number.isFinite(n) ? Math.max(max, n) : max;
        }, 0);
        const next = maxLine > 0 ? String(maxLine + 1).padStart(5, "0") : lineNumber;
        setLineNumber(next);

        // Always show item form in edit mode and reset
        setShowItemForm(true);
        setItemFormData(getInitialItemFormData(next));
    }, [effectiveEditData]);




    const tabs = [
        {
            name: "Item Details",
            fields: [
                "lineNumber",
                "item",
                "description",
                "itemPerPrice",
                "itemcode",
                "itemweight",
                "pack",
                "uom",
                "orderQty",
                "itemPOTolerance",
                "qcRequired",
                "openqty",
                "receivedqty",
                "status",
            ],
        },
    ];
    const currentTabFields = tabs[activeTab].fields;

    const labelFor = (name) => {
        const allFields = [
            ...formSections.flatMap((s) => s.fields || []),
            ...sourceAddressFieldsSO,
            ...destinationAddressFieldsSO,
            ...addItemsFieldsData,
        ];
        const found = allFields.find((f) => f.name === name);
        return found ? found.label : name;
    };

    // ---- Tab-level validation indicator helpers ----
    const headerFieldNames = React.useMemo(() => {
        const sec = formSections.find((s) => s.title === "SO Header");
        return sec ? sec.fields.map((f) => f.name) : [];
    }, []);

    const requiredFieldsBySection = React.useMemo(() => {
        const map = {};
        (formSections || []).forEach((sec) => {
            map[sec.title] = (sec.fields || [])
                .filter((f) => f && f.required)
                .map((f) => f.name);
        });
        return map;
    }, [formSections]);

    const headerRequiredFieldNames = React.useMemo(() => {
        return requiredFieldsBySection["SO Header"] || [];
    }, [requiredFieldsBySection]);

    const addressFieldNames = React.useMemo(() => {
        return [
            ...sourceAddressFieldsSO.map((f) => f.name),
            ...destinationAddressFieldsSO.map((f) => f.name),
        ];
    }, []);

    const sectionHasError = (title) => {
        if (!hasTriedSubmit) return false;
        if (!errors) return false;
        const keys = Object.keys(errors || {});
        if (keys.length === 0) return false;

        if (title === "SO Header") {
            return keys.some((k) => headerRequiredFieldNames.includes(k));
        }
        if (title === "Address") {
            return keys.some((k) => addressFieldNames.includes(k));
        }
        if (title === "Bill To") {
            if (!!formData.sameAsDestination) return false;
            const billRequired = [
                "billToName",
                "billToAddress1",
                "billToCity",
                "billToState",
                "billToCountry",
            ];
            return keys.some((k) => billRequired.includes(k));
        }
        if (title === "Invoice") {
            const sec = formSections.find((s) => s.title === "Invoice");
            const names = sec ? sec.fields.map((f) => f.name) : [];
            return keys.some((k) => names.includes(k));
        }
        if (title === "Misc") {
            const sec = formSections.find((s) => s.title === "Misc");
            const names = sec ? sec.fields.map((f) => f.name) : [];
            return keys.some((k) => names.includes(k));
        }
        if (title === "UDF") {
            const sec = formSections.find((s) => s.title === "UDF");
            const names = sec ? sec.fields.map((f) => f.name) : [];
            return keys.some((k) => names.includes(k));
        }
        return false;
    };

    const handleChange = (e) => {
        const target = e && e.target ? e.target : e;
        const { name, value, type, checked, files } = target;

        // allow switching address tab even in readOnly, but block other edits
        if (readOnly && name !== "__activeAddressTab") return;

        if (name === "orderType") return;

        if (type === "file") {
            if (readOnly) return;
            const file = files && files.length ? files[0] : null;

            setFormData((prev) => {
                const next = { ...prev, [name]: file };
                return next;
            });

            setErrors((prev) => {
                if (!prev) return {};
                const next = { ...prev };
                delete next[name];
                return next;
            });

            return;
        }

        const valToSet = type === "checkbox" ? checked : typeof value === "string" ? value.trimStart() : value;
        setFormData((prev) => {
            const next = { ...prev, [name]: valToSet };
            if (name === "totalOrderQuantity") next.totalOrderedQuantity = valToSet;
            if (name === "totalOrderedQuantity") next.totalOrderQuantity = valToSet;
            return next;
        });

        setErrors((prev) => {
            if (!prev) return {};
            const next = { ...prev };
            delete next[name];
            return next;
        });
    };

    const clearShipTo = () => {
        [
            "destinationName",
            "destinationAddress1",
            "destinationAddress2",
            "destinationAddress3",
            "destinationAddress4",
            "destinationCity",
            "destinationState",
            "destinationCountry",
            "destinationContact",
            "destinationEmail",
        ].forEach((name) => handleChange({ target: { name, value: "" } }));
    };

    const handleItemChange = (e) => {
        const target = e && e.target ? e.target : e;
        const { name, value, type, checked } = target;
        if (readOnly) return;

        setItemFormData((prev) => {
            const val = type === "checkbox" ? checked : value;

            // keep both item & skuId in sync
            if (name === "item" || name === "skuId") {
                const nextSku = val == null ? "" : String(val);
                const skuChanged = nextSku !== (prev.skuId ?? prev.item ?? "");
                return {
                    ...prev,
                    item: nextSku,
                    skuId: nextSku,
                    ...(skuChanged ? { serialConfig: null, serialDetails: [] } : {}),
                };
            }

            return { ...prev, [name]: val };
        });

        // errors cleanup
        setItemErrors((prev) => {
            if (!prev || Object.keys(prev).length === 0) return {};
            const clone = { ...prev };
            if (name in clone) {
                delete clone[name];
                return clone;
            }
            const currentLine = itemFormData.lineNumber || null;
            if (currentLine && clone[currentLine]) {
                const rowErr = { ...clone[currentLine] };
                if (name in rowErr) {
                    delete rowErr[name];
                    if (Object.keys(rowErr).length === 0) {
                        delete clone[currentLine];
                    } else {
                        clone[currentLine] = rowErr;
                    }
                }
            }
            return clone;
        });
    };

    const nextTab = () => {
        if (activeTab < tabs.length - 1) setActiveTab((s) => s + 1);
    };
    const prevTab = () => {
        if (activeTab > 0) setActiveTab((s) => s - 1);
    };

    const isItemEditing = (() => {
        const ln = (itemFormData.lineNumber ?? "").toString().padStart(5, "0");
        const editingByLine = savedData.some((r) => r.lineNumber === ln);
        const editingById =
            !!(itemFormData.id || itemFormData._id) &&
            savedData.some((r) => r.id === itemFormData.id || r._id === itemFormData._id);
        return editingByLine || editingById;
    })();

    // Only validate item form fields when Add/Update button is clicked
    const handleAdd = () => {
        if (readOnly) return;

        const itemVal = (itemFormData.item ?? itemFormData.skuId ?? "").toString().trim();
        const descVal = (itemFormData.description ?? "").toString().trim();
        const qtyRaw = itemFormData.orderQty;
        const qty = Number(qtyRaw);

        const itemErr = {};
        if (!itemVal) itemErr.item = "SKU ID is required";
        if (!descVal) itemErr.description = "Description is required";
        if (qtyRaw === "" || isNaN(qty) || qty <= 0) itemErr.orderQty = "Order Qty must be a number > 0";

        if (Object.keys(itemErr).length > 0) {
            setItemErrors(itemErr);
            toast.error("Please fix item errors before adding.");
            return;
        }

        const currentFormLine =
            itemFormData.lineNumber && itemFormData.lineNumber.toString().trim()
                ? itemFormData.lineNumber.toString().padStart(5, "0")
                : null;
        const effectiveLineNumber = currentFormLine ?? String(lineNumber).padStart(5, "0");

        const isEditingByLine = !!(currentFormLine && savedData.some((r) => r.lineNumber === currentFormLine));
        const editingId = itemFormData.id ?? itemFormData._id ?? null;
        const isEditingById = !!(editingId && savedData.some((r) => r.id === editingId || r._id === editingId));

        const baseRow = {
            ...itemFormData,
            item: itemVal,
            skuId: itemVal,
            description: descVal,
            orderQty: qty,
            lineNumber: effectiveLineNumber,
            serialDetails: compactSerialRows(itemFormData.serialDetails, qty),
        };

        const resetToNext = (padded) => setItemFormData(getInitialItemFormData(padded));

        if (isEditingByLine) {
            setSavedData((prev) =>
                prev.map((r) =>
                    r.lineNumber === currentFormLine
                        ? {
                            id: r.id ?? editingId ?? r.lineNumber,
                            _id: r._id ?? editingId ?? r.id ?? r.lineNumber,
                            ...r,
                            ...baseRow,
                        }
                        : r
                )
            );
        } else if (isEditingById) {
            setSavedData((prev) =>
                prev.map((r) =>
                    r.id === editingId || r._id === editingId ? { id: r.id ?? editingId, _id: r._id ?? editingId, ...r, ...baseRow } : r
                )
            );
        } else {
            const uniqueId = `${Date.now()}-${Math.floor(Math.random() * 10000)}`;
            const newRow = {
                ...baseRow,
                id: itemFormData.id ?? uniqueId,
                _id: itemFormData._id ?? null,
            };
            setSavedData((prev) => [...prev, newRow]);
        }

        setLineNumber((prevLn) => {
            const asNum = parseInt(prevLn, 10) || 0;
            const padded = String(asNum + 1).padStart(5, "0");
            resetToNext(padded);
            return padded;
        });

        setItemErrors({});
        setOpen(true);
        setActiveTab(0);
    };



    const handleEdit = (rowOrLineNumber) => {
        const found =
            typeof rowOrLineNumber === "object"
                ? rowOrLineNumber
                : savedData.find((r) => r.lineNumber === rowOrLineNumber);
        if (!found) return;

        const sku =
            found.skuId ?? found.item ?? found.sku ?? found._raw?.detail?.skuId ?? "";
        const desc =
            found.description ??
            found.desc ??
            found.name ??
            found.title ??
            found._raw?.detail?.description ??
            "";

        const normalized = {
            ...found,
            item: sku ?? "",
            skuId: sku ?? "",
            description: desc ?? "",
            lineNumber: (found.lineNumber ?? "").toString().padStart(5, "0"),
        };

        setItemFormData(normalized);
        setActiveTab(0);
        setShowItemForm(true);

        setTimeout(() => {
            formRef.current?.querySelector("#item-item")?.scrollIntoView({ behavior: "smooth", block: "center" });
            formRef.current?.querySelector("#item-item")?.focus();
        }, 140);
    };

    const handleDelete = (lineNumberToDelete) => {
        if (readOnly) return;
        setSavedData((prev) => prev.filter((r) => r.lineNumber !== lineNumberToDelete));
    };

    // validateAll requires at least one item
    const validateAll = () => {
        const newErrors = {};

        // Header validation: use required fields from schema
        headerRequiredFieldNames.forEach((fname) => {
            const raw = formData[fname];
            const val = raw === null || raw === undefined ? "" : String(raw).trim();
            if (val === "") newErrors[fname] = `${labelFor(fname)} is required`;
        });

        // Address (source + destination) required
        sourceAddressFieldsSO
            .filter((f) => f.required)
            .forEach((f) => {
                const val = (formData[f.name] ?? "").toString().trim();
                if (val === "") newErrors[f.name] = `${f.label} is required`;
            });
        destinationAddressFieldsSO
            .filter((f) => f.required)
            .forEach((f) => {
                const val = (formData[f.name] ?? "").toString().trim();
                if (val === "") newErrors[f.name] = `${f.label} is required`;
            });

        // Items
        const newItemErrors = {};
        if (!savedData || savedData.length === 0) {
            newErrors._noItems = "Add at least one item to the order";
        } else {
            savedData.forEach((it, idx) => {
                const rowErr = {};
                const itemVal = (it.item ?? "").toString().trim();
                const descVal = (it.description ?? "").toString().trim();
                const qtyRaw = it.orderQty;
                const qty = Number(qtyRaw);

                if (!itemVal) rowErr.item = `${labelFor("item")} is required`;
                if (!descVal) rowErr.description = `${labelFor("description")} is required`;
                if (qtyRaw === "" || isNaN(qty) || qty <= 0) rowErr.orderQty = `${labelFor("orderQty")} must be > 0`;

                if (Object.keys(rowErr).length > 0) {
                    const key = it.lineNumber || `row-${idx}`;
                    newItemErrors[key] = rowErr;
                }
            });
        }

        let firstField = null;
        if (Object.keys(newErrors).length > 0) {
            const headerFirst = headerRequiredFieldNames.find((h) => newErrors[h]);
            if (headerFirst) firstField = headerFirst;
            else {
                const srcFirst = sourceAddressFieldsSO.map((f) => f.name).find((n) => newErrors[n]);
                const destFirst = destinationAddressFieldsSO.map((f) => f.name).find((n) => newErrors[n]);

                firstField = srcFirst || destFirst || (newErrors._noItems ? "_noItems" : Object.keys(newErrors)[0]);
            }
        } else if (Object.keys(newItemErrors).length > 0) {
            firstField = Object.keys(newItemErrors)[0];
        }

        const valid = Object.keys(newErrors).length === 0 && Object.keys(newItemErrors).length === 0;
        return { valid, firstField, newErrors, newItemErrors };
    };

    const focusField = (nameOrSelector) => {
        if (!formRef.current) return;

        const tryFocus = () => {
            const elByName = formRef.current.querySelector(`[name="${nameOrSelector}"]`);
            if (elByName && typeof elByName.focus === "function") {
                elByName.focus();
                elByName.scrollIntoView({ behavior: "smooth", block: "center" });
                return true;
            }
            const elById = formRef.current.querySelector(`#${nameOrSelector}`);
            if (elById && typeof elById.focus === "function") {
                elById.focus();
                elById.scrollIntoView({ behavior: "smooth", block: "center" });
                return true;
            }
            return false;
        };

        const sourceNames = sourceAddressFieldsSO.map((f) => f.name);
        const destNames = destinationAddressFieldsSO.map((f) => f.name);

        if (sourceNames.includes(nameOrSelector)) {
            setTopActiveSection("Address");
            setFormData((prev) => ({ ...prev, __activeAddressTab: "supplier" }));
            setTimeout(() => {
                if (!tryFocus()) formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 120);
            return;
        }

        if (destNames.includes(nameOrSelector)) {
            setTopActiveSection("Address");
            setFormData((prev) => ({ ...prev, __activeAddressTab: "buyer" }));
            setTimeout(() => {
                if (!tryFocus()) formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 120);
            return;
        }

        if (tryFocus()) return;
        formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    // Submit (Create / Update)
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (readOnly) {
            onCancel && onCancel();
            return;
        }
        setHasTriedSubmit(true);

        const { valid, firstField, newErrors } = validateAll();
        setErrors(newErrors);
        setItemErrors({});

        if (!valid) {
            if (newErrors._noItems) {
                toast.error(newErrors._noItems, { position: "top-center", autoClose: 3000 });
                setTimeout(() => {
                    try {
                        const addItemsHeading = formRef.current?.querySelector("h4.text-center");
                        if (addItemsHeading && typeof addItemsHeading.scrollIntoView === "function") {
                            addItemsHeading.scrollIntoView({ behavior: "smooth", block: "center" });
                        } else {
                            formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                        }
                    } catch { }
                }, 80);
                return;
            }

            if (firstField) {
                try {
                    focusField(firstField);
                } catch { }
            }
            toast.error("Please fix required fields highlighted in red before submitting.", {
                position: "top-center",
                autoClose: 3000,
            });
            return;
        }

        const determineEditId = () => {
            return (
                effectiveEditData?._id ??
                effectiveEditData?.id ??
                effectiveEditData?.soId ??
                effectiveEditData?.soNumber ??
                null
            );
        };

        const editId = determineEditId();

        const salesOrderId =
            effectiveEditData?.salesOrderId ??
            effectiveEditData?.soId ??
            effectiveEditData?._id ??
            effectiveEditData?.id ??
            editId ??
            null;

        const lines = savedData.map(({ id, ...rest }, idx) => {
            const sp = Number(rest.itemPerPrice);
            const detail = {
                lineNumber: rest.lineNumber ?? String(idx + 1).padStart(5, "0"),
                skuId: rest.item || rest.skuId || "",
                description: rest.description || "",
                pack: rest.pack !== undefined && rest.pack !== null && rest.pack !== "" ? rest.pack : "STD",
                orderedQty: Number(rest.orderQty) || 0,
                // openQty: Number(rest.openQty) || Number(rest.orderQty) || 0,
                itemPOTolerance: rest.itemPOTolerance || null,
                qcRequired: !!rest.qcRequired,
                uom: rest.uom || "EA",
                status: rest.status || "New",
                itemWeight: rest.itemweight || null,
                itemCode: rest.itemcode || null,
                serialDetails: Array.isArray(rest.serialDetails) ? rest.serialDetails : [],
            };
            if (Number.isFinite(sp)) detail.sellingPrice = sp;
            return { salesOrderId, detail };
        });

        // compute totals
        const { totalOrdered, totalOpen } = savedData.reduce(
            (acc, r) => {
                const ord = Number(r.orderQty) || 0;
                const open = Number(r.openQty ?? r.orderQty) || 0;
                acc.totalOrdered += ord;
                acc.totalOpen += open;
                return acc;
            },
            { totalOrdered: 0, totalOpen: 0 }
        );
        const totalOrderLines = savedData.length;

        const payload = {
            salesOrderId,
            soDetails: {
                soNumber: effectiveEditData?.soNumber || formData.soNumber || null,
                externalSoNumber: formData.externalOrder || "",
                owner: formData.owner || "",
                orderType: formData.orderType || "",
                expectedReceiptDate: formData.expectedReceiptDate || "",
                status: labelToStatusCode(formData.status),
                totalOrderedQuantity: totalOrdered,
                totalOrderLines,
                openQuantity: totalOpen,
                totalWeight: formData.totalWeight || "",
                totalVolume: formData.totalVolume || "",
                totalOrderQuantity: formData.totalOrderQuantity || "",
                orderDate: formData.orderDate || "",
                closeDate: formData.closeDate || "",
                source: formData.source || "",
                trailerNumber: formData.trailerNumber || formData.carrierTrailerNumber || null,
                trailerOwner: formData.trailerOwner || formData.carrierTrailerOwner || null,
                driverName: formData.driverName || formData.carrierDriverName || null,
                departureDateTime: editId ? (formData.departureDateTime || null) : null,
                actualDeliveryDate: editId ? (formData.actualDeliveryDate || null) : null,
                proNumber: formData.proNumber || "",
                loadId: formData.loadId || "",
                door: formData.door || null,
                route: formData.route || null,
                stop: formData.stop || null,
            },
            sourceDetail: {
                customerId: formData.sourceCustomerId || "",
                locationName: formData.sourceLocationName || "",
                addressLine1: formData.sourceAddress1 || "",
                addressLine2: formData.sourceAddress2 || "",
                addressLine3: formData.sourceAddress3 || "",
                addressLine4: formData.sourceAddress4 || "",
                city: formData.sourceCity || "",
                state: formData.sourceState || "",
                country: formData.sourceCountry || "",
                contact: formData.sourceContact || "",
                email: formData.sourceEmail || "",
            },
            destination: {
                destinationName: formData.destinationName || formData.destinationName || "",
                addressLine1: formData.destinationAddress1 || "",
                addressLine2: formData.destinationAddress2 || "",
                addressLine3: formData.destinationAddress3 || "",
                addressLine4: formData.destinationAddress4 || "",
                city: formData.destinationCity || "",
                state: formData.destinationState || "",
                country: formData.destinationCountry || "",
                contact: formData.destinationContact || "",
                email: formData.destinationEmail || "",
            },
            carrier: editId
                ? {
                    carrierCode: formData.carrierCode || null,
                    carrierName: formData.carrierName || null,
                    addressLine1: formData.carrierAddress1 || null,
                    addressLine2: formData.carrierAddress2 || null,
                    city: formData.carrierCity || null,
                    state: formData.carrierState || null,
                    country: formData.carrierCountry || null,
                    phone: formData.carrierPhone || null,
                }
                : {
                    carrierCode: null,
                    carrierName: null,
                    addressLine1: null,
                    addressLine2: null,
                    city: null,
                    state: null,
                    country: null,
                    phone: null,
                },
            billTo: {
                billToCode: formData.sourceCustomerCode || formData.billToCode || "",
                billToName: formData.sourceLocationName || "",
                addressLine1: formData.sourceAddress1 || "",
                addressLine2: formData.sourceAddress2 || "",
                addressLine3: formData.sourceAddress3 || "",
                addressLine4: formData.sourceAddress4 || "",
                city: formData.sourceCity || "",
                state: formData.sourceState || "",
                country: formData.sourceCountry || "",
                contact: formData.sourceContact || "",
                email: formData.sourceEmail || "",
            },
            invoice: {
                invoiceDate: formData.invoiceDate || "",
                invoiceNumber: formData.invoiceNumber || "",
                invoiceType: formData.invoiceType || "",
                invoiceAmount: formData.invoiceAmount !== undefined ? formData.invoiceAmount : null,
                paymentTerms: formData.paymentTerms || formData.invoicePaymentTerms || "",
            },
            misc: {
                notes: formData.notes || "",
                termsOfDelivery: formData.termsOfDelivery || "",
                termsOfPayment: formData.termsOfPayment || "",
            },
            udf: {
                headerUdf1: formData.soStoHeaderUdf1 || "",
                headerUdf2: formData.soStoHeaderUdf2 || "",
                headerUdf3: formData.soStoHeaderUdf3 || "",
                headerUdf4: formData.soStoHeaderUdf4 || "",
                textUdf1: formData.soStoTextUdf1 || "",
                textUdf2: formData.soStoTextUdf2 || "",
                textUdf3: formData.soStoTextUdf3 || "",
                textUdf4: formData.soStoTextUdf4 || "",
            },
            lineItems: lines,
        };

        setLoading(true);
        (async () => {
            try {
                if (editId) {
                    if (formData.invoiceUpload && typeof formData.invoiceUpload !== "string") {
                        const fd = new FormData();
                        fd.append("payload", JSON.stringify(payload));
                        fd.append("invoiceUpload", formData.invoiceUpload);
                        const res = await updateSalesOrder(editId, fd);
                        if (res) {
                            toast.success(`SO/STO updated successfully`, { position: "top-center", autoClose: 3000 });
                        }
                    } else {
                        const res = await updateSalesOrder(editId, payload);
                        if (res) {
                            toast.success(`SO/STO updated successfully`, { position: "top-center", autoClose: 3000 });
                        }
                    }

                    const latestList = await fetchSalesOrder({});
                    setSavedData([]);
                    setItemErrors({});
                    setErrors({});
                    setOpen(false);

                    onSaved && onSaved({ updated: true, list: latestList });
                } else {
                    if (formData.invoiceUpload && typeof formData.invoiceUpload !== "string") {
                        const fd = new FormData();
                        fd.append("payload", JSON.stringify(sanitizeFormData(payload)));
                        fd.append("invoiceUpload", formData.invoiceUpload);
                        await createSalesOrder(fd); // send multipart
                    } else {
                        const data = sanitizeFormData(payload);
                        const res = await createSalesOrder(data);
                        if (res?.soNumber) {
                            toast.success(`SO Number ${res.soNumber} created successfully`, {
                                position: "top-center",
                                autoClose: 3000,
                                hideProgressBar: false,
                                closeOnClick: true,
                                pauseOnHover: true,
                                draggable: true,
                            });
                        }
                    }

                    const latestList = await fetchSalesOrder({});
                    setSoCounter((prev) => prev + 1);
                    setFormData(getInitialFormData(soCounter + 1));
                    setSavedData([]);
                    setItemErrors({});
                    setErrors({});
                    setOpen(false);

                    onSaved && onSaved({ created: true, list: latestList });
                }
            } catch (err) {
                // log/handle as needed
            } finally {
                setLoading(false);
            }
        })();
    };

    useEffect(() => {
        if (!("__activeAddressTab" in formData)) {
            setFormData((prev) => ({ ...prev, __activeAddressTab: "supplier" }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Bill To is hidden and always matches the selected customer.
    useEffect(() => {
        setFormData((prev) => {
            const nextBillTo = {
                billToCode: prev.sourceCustomerCode || prev.billToCode || "",
                billToName: prev.sourceLocationName || "",
                billToAddress1: prev.sourceAddress1 || "",
                billToAddress2: prev.sourceAddress2 || "",
                billToAddress3: prev.sourceAddress3 || "",
                billToAddress4: prev.sourceAddress4 || "",
                billToCity: prev.sourceCity || "",
                billToState: prev.sourceState || "",
                billToCountry: prev.sourceCountry || "",
                billToContact: prev.sourceContact || "",
                billToEmail: prev.sourceEmail || "",
            };
            const unchanged = Object.entries(nextBillTo).every(([key, value]) => prev[key] === value);
            if (unchanged) return prev;
            return { ...prev, ...nextBillTo };
        });
    }, [
        formData.sourceCustomerCode,
        formData.sourceLocationName,
        formData.sourceAddress1,
        formData.sourceAddress2,
        formData.sourceAddress3,
        formData.sourceAddress4,
        formData.sourceCity,
        formData.sourceState,
        formData.sourceCountry,
        formData.sourceContact,
        formData.sourceEmail,
        formData.billToCode,
    ]);

    const renderLabel = (field) => (
        <>
            {field.label} {field.required && <span className="text-danger">*</span>}
        </>
    );

    // helpers for tabs’ FieldRenderer
    const getInputTypeForField = (field) => {
        if (field.name === "orderDate") return "datetime-local";
        if (field.type === "date") return "date";
        return field.type || "text";
    };
    const getValueForField = (field, rawValue) => {
        if (field.name === "orderDate") return rawValue || "";
        return rawValue ?? "";
    };

    // === FIELD NAME ALIASES (schema vs state mismatch safeguard) ===
    const ITEM_FIELD_ALIASES = { openqty: "openQty", receivedqty: "receivedQty" };
    const normalizedAddItemFields = addItemsFieldsData.map((f) =>
        ITEM_FIELD_ALIASES[f.name] ? { ...f, name: ITEM_FIELD_ALIASES[f.name] } : f
    );

    return (
        <div ref={formRef}>
            {loading && <Loader />}
            <div className="card-body p-0">
                <form onSubmit={handleSubmit} autoComplete="off" noValidate>
                    <Tab.Container activeKey={topActiveSection} onSelect={(k) => setTopActiveSection(k)}>
                        <Nav variant="tabs" className="mb-3">
                            {formSections.filter((section) => !section.hidden).map((section) => {
                                const hasErrors = sectionHasError(section.title);
                                const isActive = topActiveSection === section.title;
                                return (
                                    <Nav.Item key={section.title}>
                                        <Nav.Link
                                            eventKey={section.title}
                                            className={`${isActive ? "active" : ""} ${hasErrors ? "text-danger fw-semibold" : ""}`}
                                            style={{
                                                position: "relative",
                                                ...(hasErrors
                                                    ? {
                                                        borderColor: "#dc3545",
                                                        color: "#dc3545",
                                                        backgroundColor: isActive ? "#f8d7da" : "transparent",
                                                    }
                                                    : {}),
                                            }}
                                        >
                                            {section.title}
                                            {hasErrors && (
                                                <span
                                                    className="badge bg-danger ms-2"
                                                    style={{ fontSize: "0.7em" }}
                                                    title="Please fix errors in this section"
                                                    aria-label="Section has errors"
                                                >
                                                    !
                                                </span>
                                            )}
                                        </Nav.Link>
                                    </Nav.Item>
                                );
                            })}
                        </Nav>

                        <Tab.Content>
                            {formSections.filter((section) => !section.hidden).map((section) => {
                                return (
                                    <Tab.Pane eventKey={section.title} key={section.title}>
                                        {section.title === "Address" ? (
                                            <div>
                                                <ul className="nav nav-pills mb-3 gap-1 bg-light p-1 rounded shadow-sm">
                                                    <li className="nav-item">
                                                        <button
                                                            type="button"
                                                            className={`nav-link d-flex align-items-center px-4 py-1 fw-semibold ${formData.__activeAddressTab === "supplier"
                                                                ? "active btn-primary text-white"
                                                                : "text-dark"
                                                                }`}
                                                            onClick={() =>
                                                                handleChange({
                                                                    target: { name: "__activeAddressTab", value: "supplier" },
                                                                })
                                                            }
                                                        >
                                                            <i className="bi bi-person-lines-fill me-2"></i> Customer
                                                        </button>
                                                    </li>
                                                    <li className="nav-item">
                                                        <button
                                                            type="button"
                                                            className={`nav-link d-flex align-items-center px-4 py-1 fw-semibold ${formData.__activeAddressTab === "buyer"
                                                                ? "active btn-primary text-white"
                                                                : "text-dark"
                                                                }`}
                                                            onClick={() =>
                                                                handleChange({
                                                                    target: { name: "__activeAddressTab", value: "buyer" },
                                                                })
                                                            }
                                                        >
                                                            <i className="bi bi-truck me-2"></i> Ship To
                                                        </button>
                                                    </li>
                                                </ul>

                                                <div className="tab-content rounded p-3">
                                                    {formData.__activeAddressTab === "supplier" ? (
                                                        <div className="tab-pane fade show active">
                                                            <div className="row g-3 mx-0">
                                                                {sourceAddressFieldsSO.map((field) => (
                                                                    <div key={field.name} className="col-12 col-sm-6 col-md-2">
                                                                        <label className="form-label">{renderLabel(field)}</label>

                                                                        {field.type === "customerLookup" ? (
                                                                            <LookupField
                                                                                name={field.name}
                                                                                value={formData[field.name] ?? ""}
                                                                                config={CUSTOMER_LOOKUP}
                                                                                editable
                                                                                error={errors[field.name] || ""}
                                                                                required={!!field.required}
                                                                                disabled={field.readOnly || readOnly}
                                                                                onChange={(e) => {
                                                                                    handleChange(e);
                                                                                    handleChange({ target: { name: "sourceCustomerId", value: "" } });
                                                                                    clearShipTo();
                                                                                }}
                                                                                onSelect={(rec) => {
                                                                                    const c = CUSTOMER_LOOKUP.normalize(rec);
                                                                                    const customerMap = {
                                                                                        [field.name]: c.name,
                                                                                        sourceCustomerId: c._id,
                                                                                        sourceCustomerCode: c.id,
                                                                                        sourceAddress1: c.address1,
                                                                                        sourceAddress2: c.address2,
                                                                                        sourceAddress3: "",
                                                                                        sourceAddress4: "",
                                                                                        sourceCity: c.city,
                                                                                        sourceState: c.state,
                                                                                        sourceCountry: c.country,
                                                                                        sourceContact: c.mobile,
                                                                                        sourceEmail: c.email,
                                                                                    };
                                                                                    Object.entries(customerMap).forEach(([k, v]) =>
                                                                                        handleChange({ target: { name: k, value: v } })
                                                                                    );
                                                                                    clearShipTo();
                                                                                }}
                                                                            />
                                                                        ) : (
                                                                            <input
                                                                                type={field.type}
                                                                                className={`form-control ${errors[field.name] ? "is-invalid" : ""}`}
                                                                                name={field.name}
                                                                                value={formData[field.name] ?? ""}
                                                                                onChange={handleChange}
                                                                                readOnly={field.readOnly || readOnly}
                                                                                style={
                                                                                    field.readOnly || readOnly
                                                                                        ? { pointerEvents: "none", backgroundColor: "#f8f9fa" }
                                                                                        : {}
                                                                                }
                                                                            />
                                                                        )}

                                                                        {!["autosearch", "supplierLookup", "customerLookup", "shipToLookup"].includes(field.type) && errors[field.name] && (
                                                                            <div className="invalid-feedback d-block">{errors[field.name]}</div>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="tab-pane fade show active">
                                                            <div className="row g-3 mx-0">
                                                                {destinationAddressFieldsSO.map((field) => (
                                                                    <div key={field.name} className="col-12 col-sm-6 col-md-2">
                                                                        <label className="form-label">{renderLabel(field)}</label>

                                                                        {field.type === "shipToLookup" ? (
                                                                            <>
                                                                                <LookupField
                                                                                    name={field.name}
                                                                                    value={formData[field.name] ?? ""}
                                                                                    config={shipToLookupConfig}
                                                                                    modalTitle="Select Ship To"
                                                                                    editable
                                                                                    error={errors[field.name] || ""}
                                                                                    required={!!field.required}
                                                                                    disabled={field.readOnly || readOnly}
                                                                                    onChange={handleChange}
                                                                                    onSelect={(rec) => {
                                                                                        const s = shipToLookupConfig.normalize(rec);
                                                                                        const shipMap = {
                                                                                            [field.name]: s.name,
                                                                                            destinationAddress1: s.address1,
                                                                                            destinationAddress2: s.address2,
                                                                                            destinationAddress3: "",
                                                                                            destinationAddress4: "",
                                                                                            destinationCity: s.city,
                                                                                            destinationState: s.state,
                                                                                            destinationCountry: s.country,
                                                                                            destinationContact: s.mobile,
                                                                                            destinationEmail: s.email,
                                                                                        };
                                                                                        Object.entries(shipMap).forEach(([k, v]) =>
                                                                                            handleChange({ target: { name: k, value: v } })
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            </>
                                                                        ) : (
                                                                            <input
                                                                                type={field.type}
                                                                                className={`form-control ${errors[field.name] ? "is-invalid" : ""}`}
                                                                                name={field.name}
                                                                                value={formData[field.name] ?? ""}
                                                                                onChange={handleChange}
                                                                                readOnly={field.readOnly || readOnly}
                                                                                style={
                                                                                    field.readOnly || readOnly
                                                                                        ? { pointerEvents: "none", backgroundColor: "#f8f9fa" }
                                                                                        : {}
                                                                                }
                                                                            />
                                                                        )}

                                                                        {!["autosearch", "supplierLookup", "customerLookup", "shipToLookup"].includes(field.type) && errors[field.name] && (
                                                                            <div className="invalid-feedback d-block">{errors[field.name]}</div>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                            </div>
                                        ) : (
                                            <div className="row g-3 mx-0">
                                                {(() => {
                                                    const isBillSection = section.title === "Bill To";
                                                    const fields = (section.fields || []).filter(
                                                        (field) => !(field.hiddenOnCreate && !effectiveEditData)
                                                    );

                                                    return fields.map((field, idx) => {
                                                        const isBillToLocked = isBillSection && !!formData.sameAsDestination;
                                                        const lockHeaderField = field?.name === "orderType";
                                                        const lockCarrierOnCreate = !!field?.disabledOnCreate && !isEditMode;
                                                        const effectiveReadOnly =
                                                            !!field?.readOnly || !!readOnly || isBillToLocked || lockHeaderField || lockCarrierOnCreate;

                                                        // Inject the standalone checkbox at the top of Bill To section
                                                        if (isBillSection && idx === 0) {
                                                            return (
                                                                <React.Fragment key="sameAsDestination-top">
                                                                    <div className="col-12">
                                                                        <div className="form-check mb-2">
                                                                            <input
                                                                                type="checkbox"
                                                                                className="form-check-input"
                                                                                id="top-sameAsDestination"
                                                                                name="sameAsDestination"
                                                                                checked={!!formData.sameAsDestination}
                                                                                onChange={handleChange}
                                                                                disabled={readOnly}
                                                                            />
                                                                            <label
                                                                                className="form-check-label"
                                                                                htmlFor="top-sameAsDestination"
                                                                            >
                                                                                Same as Ship To
                                                                            </label>
                                                                        </div>
                                                                    </div>

                                                                    <div className="col-12 col-sm-6 col-md-2" key={field.name}>
                                                                        {field.type === "select" ? (
                                                                            <>
                                                                                <label
                                                                                    className="form-label"
                                                                                    htmlFor={`top-${field.name}`}
                                                                                >
                                                                                    {renderLabel(field)}
                                                                                </label>
                                                                                <select
                                                                                    id={`top-${field.name}`}
                                                                                    className={`form-select ${effectiveReadOnly ? "bg-light" : ""
                                                                                        } ${errors[field.name] ? "is-invalid" : ""}`}
                                                                                    name={field.name}
                                                                                    value={formData[field.name] ?? ""}
                                                                                    onChange={handleChange}
                                                                                    disabled={effectiveReadOnly}
                                                                                    style={
                                                                                        effectiveReadOnly ? { pointerEvents: "none" } : {}
                                                                                    }
                                                                                >
                                                                                    <option value="">
                                                                                        {field.required ? "Select" : ""}
                                                                                    </option>
                                                                                    {(field.options || []).map((opt) =>
                                                                                        typeof opt === "object" ? (
                                                                                            <option key={opt.value} value={opt.value}>
                                                                                                {opt.label}
                                                                                            </option>
                                                                                        ) : (
                                                                                            <option key={opt} value={opt}>
                                                                                                {opt}
                                                                                            </option>
                                                                                        )
                                                                                    )}
                                                                                </select>
                                                                                {errors[field.name] && (
                                                                                    <div className="invalid-feedback d-block">
                                                                                        {errors[field.name]}
                                                                                    </div>
                                                                                )}
                                                                            </>
                                                                        ) : field.type === "multiselect" ? (
                                                                            <>
                                                                                <label
                                                                                    className="form-label"
                                                                                    htmlFor={`top-${field.name}`}
                                                                                >
                                                                                    {renderLabel(field)}
                                                                                </label>
                                                                                <select
                                                                                    id={`top-${field.name}`}
                                                                                    className={`form-select ${effectiveReadOnly ? "bg-light" : ""
                                                                                        } ${errors[field.name] ? "is-invalid" : ""}`}
                                                                                    name={field.name}
                                                                                    multiple
                                                                                    value={formData[field.name] || []}
                                                                                    onChange={(e) =>
                                                                                        handleChange({
                                                                                            target: {
                                                                                                name: field.name,
                                                                                                value: Array.from(
                                                                                                    e.target.selectedOptions,
                                                                                                    (o) => o.value
                                                                                                ),
                                                                                            },
                                                                                        })
                                                                                    }
                                                                                    disabled={effectiveReadOnly}
                                                                                    style={
                                                                                        effectiveReadOnly ? { pointerEvents: "none" } : {}
                                                                                    }
                                                                                >
                                                                                    {(field.options || []).map((opt) =>
                                                                                        typeof opt === "object" ? (
                                                                                            <option key={opt.value} value={opt.value}>
                                                                                                {opt.label}
                                                                                            </option>
                                                                                        ) : (
                                                                                            <option key={opt} value={opt}>
                                                                                                {opt}
                                                                                            </option>
                                                                                        )
                                                                                    )}
                                                                                </select>
                                                                                {errors[field.name] && (
                                                                                    <div className="invalid-feedback d-block">
                                                                                        {errors[field.name]}
                                                                                    </div>
                                                                                )}
                                                                            </>
                                                                        ) : field.type === "file" ? (
                                                                            <>
                                                                                <label
                                                                                    className="form-label"
                                                                                    htmlFor={`top-${field.name}`}
                                                                                >
                                                                                    {renderLabel(field)}
                                                                                </label>
                                                                                <input
                                                                                    id={`top-${field.name}`}
                                                                                    type="file"
                                                                                    className={`form-control ${effectiveReadOnly ? "bg-light" : ""
                                                                                        } ${errors[field.name] ? "is-invalid" : ""}`}
                                                                                    name={field.name}
                                                                                    onChange={handleChange}
                                                                                    disabled={effectiveReadOnly}
                                                                                />
                                                                                {errors[field.name] && (
                                                                                    <div className="invalid-feedback d-block">
                                                                                        {errors[field.name]}
                                                                                    </div>
                                                                                )}
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <label
                                                                                    className="form-label"
                                                                                    htmlFor={`top-${field.name}`}
                                                                                >
                                                                                    {renderLabel(field)}
                                                                                </label>
                                                                                <input
                                                                                    id={`top-${field.name}`}
                                                                                    type={getInputTypeForField(field)}
                                                                                    className={`form-control ${effectiveReadOnly ? "bg-light" : ""
                                                                                        } ${errors[field.name] ? "is-invalid" : ""}`}
                                                                                    name={field.name}
                                                                                    value={getValueForField(
                                                                                        field,
                                                                                        formData[field.name]
                                                                                    )}
                                                                                    onChange={handleChange}
                                                                                    readOnly={effectiveReadOnly}
                                                                                    style={
                                                                                        effectiveReadOnly
                                                                                            ? {
                                                                                                pointerEvents: "none",
                                                                                                backgroundColor: "#f8f9fa",
                                                                                            }
                                                                                            : {}
                                                                                    }
                                                                                />
                                                                                {errors[field.name] && (
                                                                                    <div className="invalid-feedback d-block">
                                                                                        {errors[field.name]}
                                                                                    </div>
                                                                                )}
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </React.Fragment>
                                                            );
                                                        }

                                                        return (
                                                            <div key={field.name} className="col-12 col-sm-6 col-md-2">
                                                                {field.type === "select" ? (
                                                                    <>
                                                                        <label
                                                                            className="form-label"
                                                                            htmlFor={`top-${field.name}`}
                                                                        >
                                                                            {renderLabel(field)}
                                                                        </label>
                                                                        <select
                                                                            id={`top-${field.name}`}
                                                                            className={`form-select ${effectiveReadOnly ? "bg-light" : ""
                                                                                } ${errors[field.name] ? "is-invalid" : ""}`}
                                                                            name={field.name}
                                                                            value={formData[field.name] ?? ""}
                                                                            onChange={handleChange}
                                                                            disabled={effectiveReadOnly}
                                                                            style={effectiveReadOnly ? { pointerEvents: "none" } : {}}
                                                                        >
                                                                            <option value="">
                                                                                {field.required ? "Select" : ""}
                                                                            </option>
                                                                            {(field.options || []).map((opt) =>
                                                                                typeof opt === "object" ? (
                                                                                    <option key={opt.value} value={opt.value}>
                                                                                        {opt.label}
                                                                                    </option>
                                                                                ) : (
                                                                                    <option key={opt} value={opt}>
                                                                                        {opt}
                                                                                    </option>
                                                                                )
                                                                            )}
                                                                        </select>
                                                                        {errors[field.name] && (
                                                                            <div className="invalid-feedback d-block">
                                                                                {errors[field.name]}
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                ) : field.type === "multiselect" ? (
                                                                    <>
                                                                        <label
                                                                            className="form-label"
                                                                            htmlFor={`top-${field.name}`}
                                                                        >
                                                                            {renderLabel(field)}
                                                                        </label>
                                                                        <select
                                                                            id={`top-${field.name}`}
                                                                            className={`form-select ${effectiveReadOnly ? "bg-light" : ""
                                                                                } ${errors[field.name] ? "is-invalid" : ""}`}
                                                                            name={field.name}
                                                                            multiple
                                                                            value={formData[field.name] || []}
                                                                            onChange={(e) =>
                                                                                handleChange({
                                                                                    target: {
                                                                                        name: field.name,
                                                                                        value: Array.from(
                                                                                            e.target.selectedOptions,
                                                                                            (o) => o.value
                                                                                        ),
                                                                                    },
                                                                                })
                                                                            }
                                                                            disabled={effectiveReadOnly}
                                                                            style={effectiveReadOnly ? { pointerEvents: "none" } : {}}
                                                                        >
                                                                            {(field.options || []).map((opt) =>
                                                                                typeof opt === "object" ? (
                                                                                    <option key={opt.value} value={opt.value}>
                                                                                        {opt.label}
                                                                                    </option>
                                                                                ) : (
                                                                                    <option key={opt} value={opt}>
                                                                                        {opt}
                                                                                    </option>
                                                                                )
                                                                            )}
                                                                        </select>
                                                                        {errors[field.name] && (
                                                                            <div className="invalid-feedback d-block">
                                                                                {errors[field.name]}
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                ) : field.type === "file" ? (
                                                                    <>
                                                                        <label
                                                                            className="form-label"
                                                                            htmlFor={`top-${field.name}`}
                                                                        >
                                                                            {renderLabel(field)}
                                                                        </label>
                                                                        <input
                                                                            id={`top-${field.name}`}
                                                                            type="file"
                                                                            className={`form-control ${effectiveReadOnly ? "bg-light" : ""
                                                                                } ${errors[field.name] ? "is-invalid" : ""}`}
                                                                            name={field.name}
                                                                            onChange={handleChange}
                                                                            disabled={effectiveReadOnly}
                                                                        />
                                                                        {errors[field.name] && (
                                                                            <div className="invalid-feedback d-block">
                                                                                {errors[field.name]}
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                ) : field.type === "partnerLookup" ? (
                                                                    <>
                                                                        <label
                                                                            className="form-label"
                                                                            htmlFor={`top-${field.name}`}
                                                                        >
                                                                            {renderLabel(field)}
                                                                        </label>
                                                                        <PartnerLookupField
                                                                            id={`top-${field.name}`}
                                                                            name={field.name}
                                                                            value={formData[field.name] ?? ""}
                                                                            disabled={effectiveReadOnly}
                                                                            error={errors[field.name] || ""}
                                                                            modalTitle={`Select ${field.label}`}
                                                                            onSelect={(partner) =>
                                                                                handleChange({
                                                                                    target: {
                                                                                        name: field.name,
                                                                                        value: partner?.partner_id || "",
                                                                                    },
                                                                                })
                                                                            }
                                                                        />
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <label
                                                                            className="form-label"
                                                                            htmlFor={`top-${field.name}`}
                                                                        >
                                                                            {renderLabel(field)}
                                                                        </label>
                                                                        <input
                                                                            id={`top-${field.name}`}
                                                                            type={getInputTypeForField(field)}
                                                                            className={`form-control ${effectiveReadOnly ? "bg-light" : ""
                                                                                } ${errors[field.name] ? "is-invalid" : ""}`}
                                                                            name={field.name}
                                                                            value={getValueForField(field, formData[field.name])}
                                                                            onChange={handleChange}
                                                                            readOnly={effectiveReadOnly}
                                                                            style={
                                                                                effectiveReadOnly
                                                                                    ? {
                                                                                        pointerEvents: "none",
                                                                                        backgroundColor: "#f8f9fa",
                                                                                    }
                                                                                    : {}
                                                                            }
                                                                        />
                                                                        {errors[field.name] && (
                                                                            <div className="invalid-feedback d-block">
                                                                                {errors[field.name]}
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </div>
                                                        );
                                                    });
                                                })()}
                                            </div>
                                        )}
                                    </Tab.Pane>
                                );
                            })}
                        </Tab.Content>
                    </Tab.Container>

                    <hr className="my-4" />

                    {/* Add Item form */}
                    {!readOnly && showItemForm && (
                        <>
                            <ul className="nav nav-tabs mb-3">
                                <li className="nav-item">
                                    <button
                                        type="button"
                                        className={`nav-link ${activeTab === 0 ? "active" : ""}`}
                                        onClick={() => setActiveTab(0)}
                                        aria-selected={activeTab === 0}
                                        aria-controls="item-tabpanel-0"
                                        id="item-tab-0"
                                    >
                                        Item Details
                                    </button>
                                </li>
                            </ul>

                            <div className="row g-3 mx-0">
                                {normalizedAddItemFields
                                    .filter((f) => currentTabFields.includes(ITEM_FIELD_ALIASES[f.name] || f.name))
                                    .map((field) => {
                                        const isCheckbox = field.type === "checkbox";
                                        const colClass = "col-12 col-sm-6 col-md-2";

                                        return (
                                            <div className={colClass} key={field.name} autoComplete="off">
                                                {field.name === "qcRequired" ? (
                                                    <>
                                                        <label className="form-label" htmlFor={`item-${field.name}`}>
                                                            {field.label}
                                                        </label>
                                                        <select
                                                            id={`item-${field.name}`}
                                                            className="form-select"
                                                            name={field.name}
                                                            value={itemFormData[field.name] ? "true" : "false"}
                                                            onChange={(e) =>
                                                                handleItemChange({
                                                                    target: {
                                                                        name: field.name,
                                                                        value: e.target.value === "true",
                                                                    },
                                                                })
                                                            }
                                                            disabled={readOnly}
                                                        >
                                                            <option value="true">Yes</option>
                                                            <option value="false">No</option>
                                                        </select>
                                                    </>
                                                ) : isCheckbox ? (
                                                    <div className="form-check mt-2">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            id={`item-${field.name}`}
                                                            name={field.name}
                                                            checked={!!itemFormData[field.name]}
                                                            onChange={handleItemChange}
                                                            disabled={readOnly}
                                                        />
                                                        <label className="form-check-label" htmlFor={`item-${field.name}`}>
                                                            {field.label}
                                                        </label>
                                                    </div>
                                                ) : field.name === "lineNumber" ? (
                                                    <>
                                                        <label className="form-label" htmlFor={`item-${field.name}`}>
                                                            {field.label}
                                                        </label>
                                                        <input
                                                            id={`item-${field.name}`}
                                                            type="text"
                                                            className="form-control bg-light"
                                                            name={field.name}
                                                            value={itemFormData.lineNumber || lineNumber}
                                                            readOnly
                                                        />
                                                    </>
                                                ) : field.type === "select" ? (
                                                    <>
                                                        <label className="form-label" htmlFor={`item-${field.name}`}>
                                                            {field.label} {field.required && <span className="text-danger">*</span>}
                                                        </label>
                                                        <select
                                                            id={`item-${field.name}`}
                                                            className={`form-select ${field.readOnly ? "bg-light" : ""} ${itemErrors[field.name] ? "is-invalid" : ""
                                                                }`}
                                                            name={field.name}
                                                            value={itemFormData[field.name] || ""}
                                                            onChange={handleItemChange}
                                                            disabled={field.readOnly || readOnly}
                                                        >
                                                            {(field.options || []).map((opt) =>
                                                                typeof opt === "object" ? (
                                                                    <option key={opt.value} value={opt.value}>
                                                                        {opt.label}
                                                                    </option>
                                                                ) : (
                                                                    <option key={opt} value={opt}>
                                                                        {opt}
                                                                    </option>
                                                                )
                                                            )}
                                                        </select>
                                                        {itemErrors[field.name] && (
                                                            <div className="invalid-feedback d-block">{itemErrors[field.name]}</div>
                                                        )}
                                                    </>
                                                ) : field.hasSearch ? (
                                                    <>
                                                        <label className="form-label" htmlFor={`item-${field.name}`}>
                                                            {field.label} {field.required && <span className="text-danger">*</span>}
                                                        </label>
                                                        <input
                                                            id={`item-${field.name}`}
                                                            type={field.type}
                                                            className={`form-control ${field.readOnly || readOnly ? "bg-light" : ""
                                                                } ${itemErrors[field.name] ? "is-invalid" : ""}`}
                                                            name={field.name}
                                                            value={
                                                                field.name === "skuId"
                                                                    ? itemFormData.skuId ?? itemFormData.item ?? ""
                                                                    : itemFormData[field.name] ?? ""
                                                            }
                                                            onChange={handleItemChange}
                                                            readOnly={field.readOnly || readOnly}
                                                            onKeyDown={async (e) => {
                                                                if (e.key === "Enter") {
                                                                    e.preventDefault();
                                                                    const currentVal =
                                                                        field.name === "skuId"
                                                                            ? itemFormData.skuId ?? itemFormData.item ?? ""
                                                                            : itemFormData[field.name] ?? "";
                                                                    const result = await fetchDataAndPatchFieldForSo(field, currentVal);
                                                                    if (result) {
                                                                        Object.entries(result).forEach(([key, value]) => {
                                                                            handleItemChange({ target: { name: key, value } });
                                                                        });
                                                                    }
                                                                }
                                                            }}
                                                            required={field.required}
                                                            style={
                                                                field.readOnly || readOnly
                                                                    ? { pointerEvents: "none", backgroundColor: "#f8f9fa" }
                                                                    : {}
                                                            }
                                                        />
                                                        {itemErrors[field.name] && (
                                                            <div className="invalid-feedback d-block">{itemErrors[field.name]}</div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <>
                                                        <label className="form-label" htmlFor={`item-${field.name}`}>
                                                            {field.label} {field.required && <span className="text-danger">*</span>}
                                                        </label>
                                                        <div className={field.name === "orderQty" && soSerialEnabled ? "input-group" : undefined}>
                                                            <input
                                                                id={`item-${field.name}`}
                                                                type={field.type}
                                                                className={`form-control ${field.readOnly || readOnly ? "bg-light" : ""
                                                                    } ${itemErrors[field.name] ? "is-invalid" : ""}`}
                                                                name={field.name}
                                                                value={itemFormData[field.name] ?? ""}
                                                                onChange={handleItemChange}
                                                                readOnly={field.readOnly || readOnly}
                                                                style={
                                                                    field.readOnly || readOnly
                                                                        ? { pointerEvents: "none", backgroundColor: "#f8f9fa" }
                                                                        : {}
                                                                }
                                                            />
                                                            {field.name === "orderQty" && soSerialEnabled && (
                                                                <SerialCaptureButton
                                                                    captured={countCapturedSerials(itemFormData.serialDetails, itemFormData.orderQty, soSerialColumns)}
                                                                    qty={itemFormData.orderQty}
                                                                    onClick={() => setShowSerialModal(true)}
                                                                />
                                                            )}
                                                        </div>
                                                        {itemErrors[field.name] && (
                                                            <div className="invalid-feedback d-block">{itemErrors[field.name]}</div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        );
                                    })}
                            </div>

                            <SerialCaptureModal
                                show={showSerialModal}
                                onHide={() => setShowSerialModal(false)}
                                qty={itemFormData.orderQty}
                                columns={soSerialColumns}
                                initialRows={itemFormData.serialDetails}
                                subtitle={itemFormData.skuId || itemFormData.item}
                                onSave={(rows) => {
                                    setItemFormData((prev) => ({ ...prev, serialDetails: rows }));
                                    setShowSerialModal(false);
                                }}
                            />

                            <div className="d-flex justify-content-end w-100 mt-3 mb-4">
                                <button type="button" className="btn btn-primary" onClick={handleAdd}>
                                    {isItemEditing ? "Update" : "Add"}
                                </button>
                            </div>
                        </>
                    )}

                    {savedData.length > 0 && (
                        <div className="mb-3">
                            <SaveOrderTable
                                data={savedData}
                                onEdit={!readOnly ? handleEdit : undefined}
                                onDelete={!readOnly ? handleDelete : undefined}
                                readOnly={readOnly}
                            />
                        </div>
                    )}

                    <div className="border-top pt-3 w-100 mt-4">
                        <div className="mt-3 d-flex justify-content-center gap-2">
                            <button type="button" className="btn btn-secondary me-2" onClick={onCancel}>
                                {readOnly ? "Back" : "Back"}
                            </button>
                            {!readOnly && (
                                <button type="submit" className="btn btn-primary">
                                    {effectiveEditData ? "Update SO" : "Create SO"}
                                </button>
                            )}
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}
